import { Truck, Send, Package, ScanSearch, Landmark, MapPin, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { OrderModel } from '@/types/model/Order'
import { useState } from 'react'
import { useSteadfastCourier } from '@/hooks/features/courier/useSteadfastCourier'
import { usePathaoCourier } from '@/hooks/features/courier/usePathaoCourier'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Label } from '@/components/ui/label'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { ScrollArea } from '@/components/ui/scroll-area'
import { useEnterSubmit, useToast } from '@/hooks/ui'
import { copyText } from '@/utils/copyText'
import { Copy } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { Spinner } from '@/components/ui/spinner'

interface CourierIntegrationProps {
    order: OrderModel.Order
    refreshOrder: () => void
}

export const CourierIntegration = ({ order, refreshOrder }: CourierIntegrationProps) => {
    const { success, error } = useToast()
    const {
        loading: sfLoading,
        createParcel: createSfParcel,
        trackParcel: trackSfParcel,
        cancelParcel: cancelSfParcel,
    } = useSteadfastCourier()
    const {
        loading: ptLoading,
        cities,
        zones,
        areas,
        fetchCities,
        fetchZones,
        fetchAreas,
        createParcel: createPtParcel,
        trackParcel: trackPtParcel,
        cancelParcel: cancelPtParcel,
    } = usePathaoCourier()

    const activeCourierKey = order.deliveryMethod?.key ?? order.courier_info?.delivery_method
    const isSteadfast = activeCourierKey === 'steadfast'
    const trackLoading = isSteadfast ? sfLoading : ptLoading

    const [isPathaoModalOpen, setIsPathaoModalOpen] = useState(false)
    const [pathaoData, setPathaoData] = useState({ city_id: '', zone_id: '', area_id: '' })
    const [trackingInfo, setTrackingInfo] = useState<any>(null)
    const [isTrackingOpen, setIsTrackingOpen] = useState(false)

    const handleSteadfast = async () => {
        const result = await createSfParcel(order._id)
        if (result) refreshOrder()
    }

    const openPathao = async () => {
        setIsPathaoModalOpen(true)
        await fetchCities()
    }

    const handleCityChange = async (val: string) => {
        setPathaoData(prev => ({ ...prev, city_id: val, zone_id: '', area_id: '' }))
        await fetchZones(val)
    }

    const handleZoneChange = async (val: string) => {
        setPathaoData(prev => ({ ...prev, zone_id: val, area_id: '' }))
        await fetchAreas(val)
    }

    const handlePathaoSubmit = async () => {
        if (!pathaoData.city_id || !pathaoData.zone_id || !pathaoData.area_id) {
            toast.error('Please select city, zone and area')
            return
        }

        const result = await createPtParcel({
            order_id: order._id,
            city_id: parseInt(pathaoData.city_id),
            zone_id: parseInt(pathaoData.zone_id),
            area_id: parseInt(pathaoData.area_id),
        })

        if (result) {
            setIsPathaoModalOpen(false)
            refreshOrder()
        }
    }

    const handleTrack = async () => {
        const trackFn = isSteadfast ? trackSfParcel : trackPtParcel
        const result = await trackFn(order._id)
        if (result) {
            setTrackingInfo(result)
            setIsTrackingOpen(true)
        }
    }

    const handleCancel = async () => {
        const cancelFn = isSteadfast ? cancelSfParcel : cancelPtParcel
        const result = await cancelFn(order._id)
        if (result) refreshOrder()
    }

    useEnterSubmit(handlePathaoSubmit, isPathaoModalOpen)

    const hasParcel = !!(order.courier_info?.consignment_id)

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between gap-2">
                <div>
                    <p className="text-sm font-semibold text-ink">Courier</p>
                    <p className="text-sm text-text-2">Send this order to a delivery partner.</p>
                </div>
                {hasParcel && (
                    <button
                        type="button"
                        className="group flex shrink-0 items-center gap-1"
                        title="Copy consignment ID"
                        onClick={async () => {
                            try {
                                await copyText(String(order.courier_info?.consignment_id))
                                success('Consignment ID copied.')
                            } catch (err) {
                                error('Copy failed.')
                            }
                        }}
                    >
                        <Badge tone="success">ID: {order.courier_info?.consignment_id}</Badge>
                        <Copy className="size-3 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
                    </button>
                )}
            </div>

            {!hasParcel ? (
                <div className="grid grid-cols-2 gap-2">
                    <Button variant="outline" onClick={handleSteadfast} disabled={sfLoading}>
                        {sfLoading ? <Spinner /> : <Send />}
                        Steadfast
                    </Button>

                    <Button variant="outline" onClick={openPathao} disabled={ptLoading}>
                        {ptLoading ? <Spinner /> : <Package />}
                        Pathao
                    </Button>
                </div>
            ) : (
                <div className="flex items-center gap-3 rounded-md border border-border bg-subdued p-3">
                    <Truck className="size-4 shrink-0 text-text-2" />
                    <div className="min-w-0 flex-1">
                        <p className="text-xs text-muted-foreground">Carrier</p>
                        <p className="truncate text-sm font-medium capitalize text-ink">{order.courier_info?.courier_name ?? activeCourierKey}</p>
                    </div>
                    <TooltipProvider delayDuration={100}>
                        <div className="flex items-center gap-1">
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        variant="outline"
                                        size="icon"
                                        aria-label="Track parcel"
                                        onClick={handleTrack}
                                        disabled={trackLoading}>
                                        {trackLoading ? <Spinner /> : <ScanSearch />}
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent side="top">Track</TooltipContent>
                            </Tooltip>

                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        variant="outline"
                                        size="icon"
                                        aria-label="Cancel parcel"
                                        onClick={handleCancel}
                                        disabled={trackLoading}
                                        className="text-error hover:text-error">
                                        {trackLoading ? <Spinner /> : <X />}
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent side="top">Cancel parcel</TooltipContent>
                            </Tooltip>
                        </div>
                    </TooltipProvider>
                </div>
            )}

            {/* Pathao Config Modal */}
            <Dialog open={isPathaoModalOpen} onOpenChange={setIsPathaoModalOpen}>
                <DialogContent size="md">
                    <DialogHeader>
                        <DialogTitle>Create Pathao shipment</DialogTitle>
                        <DialogDescription>
                            Choose where Pathao should deliver this order.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4">
                        <div className="space-y-1.5">
                            <Label className="text-sm text-ink">City</Label>
                            <Select value={pathaoData.city_id} onValueChange={handleCityChange}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Select city" />
                                </SelectTrigger>
                                <SelectContent>
                                    {cities.map(c => (
                                        <SelectItem key={c.city_id} value={c.city_id.toString()}>{c.city_name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-1.5">
                            <Label className="text-sm text-ink">Zone</Label>
                            <Select
                                value={pathaoData.zone_id}
                                onValueChange={handleZoneChange}
                                disabled={!pathaoData.city_id}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Select zone" />
                                </SelectTrigger>
                                <SelectContent>
                                    {zones.map(z => (
                                        <SelectItem key={z.zone_id} value={z.zone_id.toString()}>{z.zone_name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-1.5">
                            <Label className="text-sm text-ink">Area</Label>
                            <Select
                                value={pathaoData.area_id}
                                onValueChange={(val) => setPathaoData(prev => ({ ...prev, area_id: val }))}
                                disabled={!pathaoData.zone_id}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Select area" />
                                </SelectTrigger>
                                <SelectContent>
                                    {areas.map(a => (
                                        <SelectItem key={a.area_id} value={a.area_id.toString()}>{a.area_name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsPathaoModalOpen(false)}>Cancel</Button>
                        <Button onClick={handlePathaoSubmit} disabled={ptLoading}>
                            {ptLoading && <Spinner />}
                            Create shipment
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Tracking Modal */}
            <Dialog open={isTrackingOpen} onOpenChange={setIsTrackingOpen}>
                <DialogContent size="sm" className="flex flex-col">
                    <DialogHeader>
                        <DialogTitle>Parcel tracking</DialogTitle>
                        <DialogDescription>
                            Latest delivery updates from the courier.
                        </DialogDescription>
                    </DialogHeader>

                    {trackingInfo && (
                        <ScrollArea className="flex-1 overflow-y-auto">
                            <div className="space-y-5">
                                <div className="flex items-center justify-between gap-3 rounded-md border border-border bg-subdued p-3">
                                    <div>
                                        <p className="text-xs text-muted-foreground">Tracking code</p>
                                        <p className="text-sm font-semibold text-ink">{trackingInfo.tracking_code}</p>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-xs text-muted-foreground">Status</p>
                                        <Badge tone="info" className="capitalize">{trackingInfo.status}</Badge>
                                    </div>
                                </div>

                                <div className="space-y-3">
                                    <h4 className="flex items-center gap-2 text-sm font-semibold text-ink">
                                        <Landmark className="size-4 text-text-2" />
                                        History
                                    </h4>
                                    <div className="ml-2 space-y-4 border-l border-border pl-5">
                                        {trackingInfo.history?.map((evt: any, i: number) => (
                                            <div key={i} className="relative">
                                                <div className={cn(
                                                    'absolute -left-[1.6rem] top-1 size-2.5 rounded-full border-2 bg-card',
                                                    i === 0 ? 'border-ink bg-ink' : 'border-border'
                                                )} />
                                                <div className="space-y-0.5">
                                                    <p className="text-sm font-medium capitalize text-ink">{evt.status}</p>
                                                    <p className="text-xs text-muted-foreground">{evt.time}</p>
                                                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                                        <MapPin className="size-3.5" />
                                                        {evt.location}
                                                    </div>
                                                    <p className="text-sm text-text-2">{evt.message}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </ScrollArea>
                    )}

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsTrackingOpen(false)}>Close</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    )
}
